import { makeStyles } from "@material-ui/core/styles";

/** Shared look of the rule screens; same palette as the Tools and Message templates pages. */
export const useRuleStyles = makeStyles({
  page: {
    backgroundColor: "#f7f9fc",
    minHeight: "calc(100vh - 64px)",
    paddingTop: "16px",
    paddingBottom: "32px",
  },
  crumbs: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "center",
    listStyle: "none",
    margin: "0 0 8px",
    padding: 0,
    fontSize: "13px",
    color: "#475467",
    "& a": { color: "#1f5fbf", textDecoration: "none", minHeight: "24px" },
    "& a:hover": { textDecoration: "underline" },
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  title: { fontSize: "28px", fontWeight: 700, color: "#101828" },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  subtitle: {
    marginTop: "4px",
    fontSize: "14px",
    color: "#475467",
    maxWidth: "760px",
  },
  filters: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    padding: "16px",
    marginBottom: "12px",
  },
  filterField: { minWidth: "200px" },
  filterFieldWide: { width: "280px", maxWidth: "100%" },
  filterFieldNarrow: { minWidth: "150px" },
  filterFieldGb: { width: "320px", maxWidth: "100%" },
  cell: { padding: "8px", fontSize: "13px", verticalAlign: "top" },
  headerCell: {
    padding: "8px",
    fontSize: "12px",
    fontWeight: 700,
    backgroundColor: "#f2f5fa",
    whiteSpace: "nowrap",
  },
  number: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  // Column sizing: `fit` columns shrink to their content; governing bodies wrap within 20%.
  fit: { width: "1%", whiteSpace: "nowrap" },
  gbColumn: { width: "20%" },
  stamp: {
    display: "flex",
    flexDirection: "column",
    whiteSpace: "nowrap",
    "& > span + span": { fontSize: "12px" },
  },
  nameLink: {
    font: "inherit",
    textAlign: "left",
    color: "#1f5fbf",
    "&:focus-visible": { outline: "2px solid #1f5fbf", outlineOffset: "2px" },
  },
  mono: { fontFamily: "monospace", fontSize: "12px" },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  chipActive: { backgroundColor: "#e3f3e8", color: "#1f7a3f", fontWeight: 600 },
  chipDeprecated: {
    backgroundColor: "#fff3dc",
    color: "#9a5b00",
    fontWeight: 600,
  },
  chipWarn: { backgroundColor: "#fff3dc", color: "#9a5b00" },
  chipNeutral: { backgroundColor: "#f2f4f7", color: "#475467" },
  touch: { minHeight: "44px", minWidth: "44px" },
  iconButton: { width: "44px", height: "44px" },
  actions: { display: "flex", gap: "4px", flexWrap: "nowrap" },
  empty: { textAlign: "center", padding: "32px 12px", color: "#475467" },
  progressWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 0",
  },
  muted: { color: "#667085", fontSize: "13px" },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "16px",
  },
  dialogForm: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: "min(520px, 80vw)",
  },
  // editor
  editor: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
    gap: "0",
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  editorPane: {
    padding: "16px 20px",
    minWidth: 0,
    "& + &": { borderLeft: "1px solid #e4e7ec" },
    "@media (max-width: 900px)": {
      "& + &": { borderLeft: 0, borderTop: "1px solid #e4e7ec" },
    },
  },
  paneTitle: {
    fontSize: "12px",
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "#667085",
    margin: "0 0 12px",
    fontWeight: 700,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
  },
  schemaNote: {
    marginTop: "12px",
    padding: "10px 12px",
    backgroundColor: "#f7f9fc",
    border: "1px dashed #d0d5dd",
    borderRadius: "6px",
    fontSize: "13px",
  },
  stepper: {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #c4c9d4",
    borderRadius: "6px",
    overflow: "hidden",
    "& input": {
      width: "56px",
      textAlign: "center",
      border: 0,
      borderLeft: "1px solid #c4c9d4",
      borderRight: "1px solid #c4c9d4",
      minHeight: "42px",
      font: "inherit",
      MozAppearance: "textfield",
    },
    "& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
    },
  },
  stepperLabel: { fontSize: "12px", color: "#667085", marginBottom: "4px" },
  roundOrder: { marginTop: "4px" },
  roundOrderRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: "16px",
    marginBottom: "4px",
  },
  roundStrip: { display: "flex", flexWrap: "wrap", gap: "4px" },
  roundCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
  },
  roundNumber: { fontSize: "11px", color: "#667085" },
  roundToggle: {
    width: "34px",
    height: "34px",
    borderRadius: "6px",
    font: "inherit",
    fontWeight: 700,
    cursor: "pointer",
    "&:disabled": { cursor: "default", opacity: 0.6 },
    "&:focus-visible": { outline: "2px solid #1d4ed8", outlineOffset: "2px" },
  },
  roundVoluntary: {
    backgroundColor: "#1d4ed8",
    border: "1px solid #1d4ed8",
    color: "#fff",
  },
  roundOptional: {
    backgroundColor: "#fff",
    border: "1px solid #98a2b3",
    color: "#344054",
  },
  classification: { display: "flex", flexDirection: "column", gap: "4px" },
  inlineHelp: { fontSize: "12px", color: "#667085" },
  ruleList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  ruleRow: {
    display: "grid",
    gridTemplateColumns: "44px minmax(0, 1fr) auto",
    gap: "8px",
    alignItems: "center",
    padding: "6px 8px",
    border: "1px solid #d0d5dd",
    borderRadius: "6px",
    backgroundColor: "#fff",
  },
  ruleRowPinned: { backgroundColor: "#f7f9fc" },
  ruleRowDragging: {
    boxShadow: "0 4px 16px rgba(16,24,40,0.16)",
    opacity: 0.85,
  },
  ruleRowNew: { borderColor: "#1f5fbf" },
  ruleText: {
    minWidth: 0,
    fontSize: "14px",
    fontWeight: 500,
    color: "#101828",
  },
  ruleMeta: { fontSize: "12px", color: "#667085", marginTop: "2px" },
  grip: { cursor: "grab", color: "#98a2b3", touchAction: "none" },
  preview: {
    marginTop: "16px",
    padding: "12px 14px",
    borderRadius: "6px",
    backgroundColor: "#f7f9fc",
    border: "1px solid #d0d5dd",
    fontSize: "14px",
    whiteSpace: "pre-line",
  },
  previewLabel: {
    fontSize: "11px",
    letterSpacing: ".04em",
    textTransform: "uppercase",
    color: "#667085",
    marginBottom: "4px",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "center",
    padding: "12px 20px",
    borderTop: "1px solid #e4e7ec",
    backgroundColor: "#f7f9fc",
  },
  footerActions: { display: "flex", gap: "8px", flexWrap: "wrap" },
  // add-rule dialog
  steps: {
    display: "flex",
    borderBottom: "1px solid #e4e7ec",
    margin: "0 -24px 16px",
    "& > div": {
      flex: 1,
      padding: "8px 12px",
      fontSize: "12px",
      color: "#667085",
      borderBottom: "2px solid transparent",
    },
  },
  stepOn: {
    color: "#1f5fbf !important",
    borderBottomColor: "#1f5fbf !important",
    fontWeight: 600,
  },
  stepDone: { color: "#1f7a3f !important" },
  categoryChips: { display: "flex", gap: "8px", flexWrap: "wrap" },
  categoryChip: { minHeight: "44px", fontSize: "14px" },
  typeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "8px",
  },
  typeCard: {
    font: "inherit",
    textAlign: "left",
    minHeight: "44px",
    padding: "10px 12px",
    border: "1px solid #d0d5dd",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#101828",
    cursor: "pointer",
    "&[aria-pressed='true']": {
      borderColor: "#1f5fbf",
      backgroundColor: "#e6eefb",
    },
    "&:focus-visible": { outline: "2px solid #1f5fbf", outlineOffset: "2px" },
    "& small": {
      display: "block",
      color: "#667085",
      fontSize: "12px",
      marginTop: "2px",
    },
  },
  scopeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "12px",
  },
  fieldGroupLabel: { fontSize: "12px", color: "#667085", marginBottom: "4px" },
  toggle: { minHeight: "40px", textTransform: "none" },
  bank: { fontFamily: "monospace" },
  banner: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    padding: "10px 12px",
    borderRadius: "6px",
    backgroundColor: "#fff3dc",
    border: "1px solid #9a5b00",
    fontSize: "13px",
  },
  bannerOk: { backgroundColor: "#e3f3e8", borderColor: "#1f7a3f" },
  dialogFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
    padding: "12px 0 4px",
  },
  dialogFooterGroup: { display: "flex", gap: "8px", flexWrap: "wrap" },
});
